import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const TestimonialsPage = () => {
  const topRowRef = useRef(null);
  const bottomRowRef = useRef(null);

  // Sample testimonials data
  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      company: "TechCorp Inc.",
      role: "CEO",
      content: "FlowMatic has revolutionized our workflow. The automation tools saved us 40 hours per week!",
      rating: 5,
      avatar: "SJ"
    },
    {
      id: 2,
      name: "Michael Chen",
      company: "StartupXYZ",
      role: "Founder",
      content: "Incredible platform! The ease of use and powerful features exceeded all our expectations.",
      rating: 5,
      avatar: "MC"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      company: "Global Solutions",
      role: "Operations Director",
      content: "The best automation platform we've ever used. Customer support is outstanding!",
      rating: 5,
      avatar: "ER"
    },
    {
      id: 4,
      name: "David Thompson",
      company: "InnovateLab",
      role: "CTO",
      content: "Game-changing technology that transformed our entire business process. Highly recommended!",
      rating: 5,
      avatar: "DT"
    },
    {
      id: 5,
      name: "Lisa Wang",
      company: "DataFlow Systems",
      role: "VP Engineering",
      content: "Outstanding platform with incredible ROI. We've seen 300% efficiency improvement!",
      rating: 5,
      avatar: "LW"
    },
    {
      id: 6,
      name: "James Wilson",
      company: "CloudFirst",
      role: "Product Manager",
      content: "The automation capabilities are mind-blowing. It's like having a team of experts working 24/7.",
      rating: 5,
      avatar: "JW"
    },
    {
      id: 7,
      name: "Anna Martinez",
      company: "ScaleUp Ventures",
      role: "COO",
      content: "Perfect solution for our growing company. The setup was seamless and results were immediate.",
      rating: 5,
      avatar: "AM"
    },
    {
      id: 8,
      name: "Robert Kim",
      company: "FutureTech",
      role: "Head of Operations",
      content: "Exceptional platform that delivered exactly what we needed. The ROI was evident within weeks.",
      rating: 5,
      avatar: "RK"
    }
  ];

  // Duplicate testimonials for seamless loop
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  useEffect(() => {
    const topRow = topRowRef.current;
    const bottomRow = bottomRowRef.current;

    if (!topRow || !bottomRow) return;

    // Add CSS classes for animation
    topRow.style.animation = 'scrollRight 30s linear infinite';
    bottomRow.style.animation = 'scrollLeft 30s linear infinite';

    // Add keyframes to document if not already added
    if (!document.getElementById('testimonials-keyframes')) {
      const style = document.createElement('style');
      style.id = 'testimonials-keyframes';
      style.textContent = `
        @keyframes scrollRight {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scrollLeft {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      if (topRow) topRow.style.animation = '';
      if (bottomRow) bottomRow.style.animation = '';
    };
  }, []);

  return (
    <section className="relative min-h-screen bg-white py-20 overflow-hidden">
      {/* Background Tech Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="testimonials-pattern" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
                <circle cx="25" cy="25" r="0.8" fill="#3b82f6" opacity="0.1"/>
                <line x1="0" y1="25" x2="50" y2="25" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.05"/>
                <line x1="25" y1="0" x2="25" y2="50" stroke="#06b6d4" strokeWidth="0.3" opacity="0.05"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#testimonials-pattern)"/>
          </svg>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 font-slim">
            See what our customers say about us!
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* 3D Carousel Container */}
        <div className="relative h-96 perspective-1000">
          {/* Top Row - Moving Right */}
          <div className="absolute top-0 left-0 w-full h-48 overflow-hidden">
            <div
              ref={topRowRef}
              className="flex space-x-6 h-full"
              style={{ width: '200%' }}
            >
              {duplicatedTestimonials.map((testimonial, index) => (
                <motion.div
                  key={`top-${testimonial.id}-${index}`}
                  className="flex-shrink-0 w-80 h-44"
                  style={{
                    transform: 'rotateY(-15deg)',
                    transformStyle: 'preserve-3d'
                  }}
                  whileHover={{
                    scale: 1.05,
                    rotateY: -5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="w-full h-full bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex flex-col justify-between overflow-hidden">
                    {/* Stars */}
                    <div className="flex space-x-1 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>

                    {/* Content */}
                    <p className="text-gray-700 text-xs leading-tight mb-3 flex-grow overflow-hidden">
                      "{testimonial.content}"
                    </p>

                    {/* Author */}
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        {testimonial.avatar}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-xs truncate">{testimonial.name}</h4>
                        <p className="text-gray-600 text-xs truncate">{testimonial.role} at {testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom Row - Moving Left */}
          <div className="absolute bottom-0 left-0 w-full h-48 overflow-hidden">
            <div
              ref={bottomRowRef}
              className="flex space-x-6 h-full"
              style={{ width: '200%' }}
            >
              {duplicatedTestimonials.map((testimonial, index) => (
                <motion.div
                  key={`bottom-${testimonial.id}-${index}`}
                  className="flex-shrink-0 w-80 h-44"
                  style={{
                    transform: 'rotateY(15deg)',
                    transformStyle: 'preserve-3d'
                  }}
                  whileHover={{
                    scale: 1.05,
                    rotateY: 5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="w-full h-full bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 flex flex-col justify-between overflow-hidden">
                    {/* Stars */}
                    <div className="flex space-x-1 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>

                    {/* Content */}
                    <p className="text-gray-700 text-xs leading-tight mb-3 flex-grow overflow-hidden">
                      "{testimonial.content}"
                    </p>

                    {/* Author */}
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        {testimonial.avatar}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-xs truncate">{testimonial.name}</h4>
                        <p className="text-gray-600 text-xs truncate">{testimonial.role} at {testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Gradient Fades */}
        <div className="absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-white to-transparent z-20 pointer-events-none"></div>
        <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-white to-transparent z-20 pointer-events-none"></div>
      </div>
    </section>
  );
};

export default TestimonialsPage;
